import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, ArrowRight, Minus, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NgapAct } from '../../../../types/ngap';
import { TimePeriod, PERIOD_LABELS, PERIOD_MULTIPLIERS, DEFAULT_QUANTITIES } from '../../types';
import { calculateActRevenueEvolution } from '../../../../utils/simulatorCalculations';
import { formatTarif, getEffectiveActCotationValue } from '../../../../utils/pricingUtils';

interface EmbeddedRevenueSimulatorProps {
  act: NgapAct;
  darkMode: boolean;
}

const PERIODS: TimePeriod[] = ['day', 'week', 'month', 'year'];

const EmbeddedRevenueSimulator: React.FC<EmbeddedRevenueSimulatorProps> = ({ act, darkMode }) => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<TimePeriod>('month');
  const [displayQuantity, setDisplayQuantity] = useState<number>(DEFAULT_QUANTITIES.month);
  const [isDrom, setIsDrom] = useState(false);

  const dailyQuantity = useMemo(() => displayQuantity / PERIOD_MULTIPLIERS[period], [displayQuantity, period]);
  const evolution = useMemo(() => calculateActRevenueEvolution(act, dailyQuantity, period, isDrom, 0, undefined, undefined, 'none'), [act, dailyQuantity, period, isDrom]);

  const handlePeriodChange = (newPeriod: TimePeriod) => { setPeriod(newPeriod); setDisplayQuantity(DEFAULT_QUANTITIES[newPeriod]); };
  const handleQuantityChange = (delta: number) => { setDisplayQuantity((prev) => Math.max(1, prev + delta)); };
  const goToFullSimulator = () => { navigate('/simulateur'); };

  const now = new Date();
  const futureRevenue = (evolution.july2027 && evolution.july2027.date > now ? evolution.july2027.value : undefined)
    ?? (evolution.september2026 && evolution.september2026.date > now ? evolution.september2026.value : undefined)
    ?? (evolution.may2026 && evolution.may2026.date > now ? evolution.may2026.value : undefined)
    ?? (evolution.janv2026 && evolution.janv2026.date > now ? evolution.janv2026.value : undefined)
    ?? evolution.current.value;
  const diff = futureRevenue - evolution.current.value;
  const diffPct = evolution.current.value > 0 ? (diff / evolution.current.value) * 100 : 0;
  const hasFuture = futureRevenue !== evolution.current.value;

  const btnSelected = 'bg-appines-petrol text-appines-white';
  const btnDefault = darkMode ? 'bg-appines-white/10 text-appines-white/65 hover:bg-appines-white/15' : 'bg-[rgba(1,42,54,0.06)] text-appines-petrol/65 hover:bg-[rgba(1,42,54,0.10)]';
  const labelClass = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/45';

  return (
    <section aria-labelledby="embedded-simulator-heading"
      className={`rounded-xl border p-6 shadow-sm ${darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'}`}>
      <div className="flex items-center mb-4">
        <Calculator className="h-6 w-6 mr-2 text-appines-emerald" />
        <h2 id="embedded-simulator-heading" className={`text-xl font-bold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>
          Simulateur de revenus pour {act.lettre_cle} {getEffectiveActCotationValue(act)}
        </h2>
      </div>
      <p className={`text-sm mb-6 ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>
        Estime tes revenus pour cet acte selon la période, la quantité et la zone.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className={`block text-xs font-semibold mb-2 uppercase tracking-wide ${labelClass}`}>Période</label>
          <div className="flex gap-1 flex-wrap">
            {PERIODS.map((p) => (
              <button key={p} type="button" onClick={() => handlePeriodChange(p)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${period === p ? btnSelected : btnDefault}`}>
                {PERIOD_LABELS[p]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={`block text-xs font-semibold mb-2 uppercase tracking-wide ${labelClass}`}>Quantité par {PERIOD_LABELS[period].toLowerCase()}</label>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => handleQuantityChange(-1)} aria-label="Diminuer la quantité"
              className={`p-2 rounded-md transition-colors ${btnDefault}`}><Minus className="h-4 w-4" /></button>
            <input type="number" min={1} value={displayQuantity}
              onChange={(e) => setDisplayQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className={`w-20 text-center px-2 py-2 rounded-md border text-sm font-semibold focus:ring-2 focus:ring-appines-emerald/25 focus:border-appines-emerald ${darkMode ? 'bg-appines-petrol border-appines-white/20 text-appines-white' : 'bg-appines-white border-[rgba(1,42,54,0.2)] text-appines-black'}`}
            />
            <button type="button" onClick={() => handleQuantityChange(1)} aria-label="Augmenter la quantité"
              className={`p-2 rounded-md transition-colors ${btnDefault}`}><Plus className="h-4 w-4" /></button>
          </div>
        </div>

        <div>
          <label className={`block text-xs font-semibold mb-2 uppercase tracking-wide ${labelClass}`}>Zone géographique</label>
          <div className="flex gap-1">
            <button type="button" onClick={() => setIsDrom(false)}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${!isDrom ? btnSelected : btnDefault}`}>Métropole</button>
            <button type="button" onClick={() => setIsDrom(true)}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isDrom ? btnSelected : btnDefault}`}>DROM</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className={`p-4 rounded-lg border-2 border-appines-emerald ${darkMode ? 'bg-appines-emerald/10' : 'bg-appines-glass/20'}`}>
          <div className="text-xs font-semibold uppercase tracking-wide mb-1 text-appines-emerald">Revenus actuels</div>
          <div className={`text-2xl font-bold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>{formatTarif(evolution.current.value)} €</div>
          <div className="text-xs mt-1 text-appines-emerald">par {PERIOD_LABELS[period].toLowerCase()}</div>
        </div>

        <div className={`p-4 rounded-lg border-2 ${hasFuture ? 'border-appines-emerald bg-appines-glass/15' : darkMode ? 'border-appines-white/12 bg-appines-white/5' : 'border-[rgba(1,42,54,0.12)] bg-[rgba(1,42,54,0.04)]'}`}>
          <div className={`text-xs font-semibold uppercase tracking-wide mb-1 flex items-center ${hasFuture ? 'text-appines-emerald' : darkMode ? 'text-appines-white/45' : 'text-appines-petrol/45'}`}>
            <TrendingUp className="h-3 w-3 mr-1" />{hasFuture ? 'Après revalorisations' : 'Pas de revalorisation prévue'}
          </div>
          <div className={`text-2xl font-bold ${hasFuture ? darkMode ? 'text-appines-white' : 'text-appines-petrol' : darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}`}>
            {formatTarif(futureRevenue)} €
          </div>
          {hasFuture && (
            <div className="text-xs mt-1 font-medium text-appines-emerald">
              +{formatTarif(diff)} € ({diffPct > 0 ? '+' : ''}{diffPct.toFixed(1)} %)
            </div>
          )}
        </div>
      </div>

      <button type="button" onClick={goToFullSimulator}
        className="w-full inline-flex items-center justify-center px-4 py-3 bg-appines-emerald hover:bg-[#22b886] text-appines-petrol font-semibold rounded-lg shadow-sm transition-all duration-200">
        Ouvrir le simulateur complet <ArrowRight className="h-4 w-4 ml-2" />
      </button>
    </section>
  );
};

export default React.memo(EmbeddedRevenueSimulator);
